import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Translate } from 'react-localize-redux';
import moment from 'moment';
import InputField from '@components/form/input';
import Datepicker from '@components/form/datepicker';
import Flex from '@components/layout/flex';
import { motion } from 'framer-motion';
import Typography from '@components/basics/typography';
import { TourEvent } from '@common/types/traclink/tour-event';
import { rgba } from 'polished';
import tracpageTheme from '@shared-tracpage/theme';

const opacity = {
  visible: {
    opacity: 1,
    scaleY: 1,
    originY: 0,
    transition: {
      ease: [0.0, 0.0, 0.25, 1.0],
      duration: 0.5,
    },
  },
  hidden: {
    opacity: 0,
    scaleY: 0,
  },
};

type Props = {
  className?: string;
  onEdit: (tour: TourEvent) => void;
  tour: TourEvent;
  loading?: boolean;
  isLight?: boolean;
};

export const TracpageEditTour: FC<Props> = ({
  className,
  onEdit,
  tour,
  loading = false,
  isLight = false,
}) => {
  const [eventName, setEventName] = useState<string>(tour.title);
  const [place, setPlace] = useState<string>(tour.place);
  const [date, setDate] = useState<string>(
    tour?.dateTime ? tour.dateTime.toString() : ''
  );
  const [link, setLink] = useState<string>(tour.link);
  const [error, setError] = useState<string>('');
  const [activeDate] = useState(moment(new Date()));

  useEffect(() => {
    setError('');
  }, [eventName, place, date, link]);

  const getMinDate = () => moment().startOf('day').add(2, 'days');

  const handleEditTour = () => {
    if (link && !link.match(/^(http|https):\/\//)) {
      setError('ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.LINK_ERROR');
      return;
    }
    if (!eventName || !place || !date || !link) {
      setError('ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.ERROR');
      return;
    }

    onEdit({
      ...tour,
      title: eventName,
      place: place,
      dateTime: new Date(date),
      link,
    });
  };

  return (
    <Wrapper
      className={className || ''}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={opacity}
    >
      <StyledInput
        isLight={isLight}
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.EVENT_NAME" />
        }
        value={eventName}
        onChange={(value) => setEventName(value)}
      />
      {/* TODO: DEVTEAM-845 - Add autocomplete with locations from google maps */}
      <StyledInput
        isLight={isLight}
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.CITY" />
        }
        value={place}
        onChange={(value) => setPlace(value)}
      />
      <StyledDatepicker
        isLight={isLight}
        title={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.DATE" />
        }
        value={moment(date).format('MMMM DD, YYYY')}
        active={activeDate}
        min={getMinDate()}
        onChange={(value) => setDate(moment(value).format('MMMM DD, YYYY'))}
        withoutIcon
      />
      <StyledInput
        isLight={isLight}
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.LINK" />
        }
        value={link}
        onChange={(value) => setLink(value)}
      />

      {error && (
        <ErrorTypography type="small" weight="700">
          <Translate id={error} />
        </ErrorTypography>
      )}

      <Flex
        style={{ marginTop: 21 }}
        justifyContent="flex-end"
        alignItems="center"
      >
        <SubmitButton onClick={handleEditTour} disabled={loading}>
          <Translate id="PROFILE.EVENTS.SUBMIT" />
        </SubmitButton>
      </Flex>
    </Wrapper>
  );
};

const StyledDatepicker = styled(Datepicker)<{ isLight: boolean }>`
  & > :nth-child(1) {
    background-color: ${({ isLight }) =>
      rgba(tracpageTheme[isLight ? 'light' : 'dark'].colors.primary, 0.05)};
    border: 0;
    border-radius: 0;
    & > div {
      & > div > .dropdown-text {
        color: ${({ theme, value, isLight }) =>
          value
            ? tracpageTheme[isLight ? 'light' : 'dark'].colors.primary
            : theme.colors.secondaryDark};
      }
      & > .label {
        color: ${({ theme }) => theme.colors.inputLabel};
      }
    }
  }
  & > :nth-child(2) {
    & > .dropdown-list-container {
      filter: none;
    }
  }
`;

const StyledInput = styled(InputField)<{ isLight: boolean }>`
  background-color: ${({ isLight }) =>
    rgba(tracpageTheme[isLight ? 'light' : 'dark'].colors.primary, 0.05)};
  border: 0;
  border-radius: 0;

  & > input {
    color: ${({ isLight }) =>
      tracpageTheme[isLight ? 'light' : 'dark'].colors.primary};
  }
`;

const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 12px 16px;
  border-radius: 90px;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const Wrapper = styled(motion.div)`
  width: 100%;
  display: grid;
  grid-gap: 3px;
`;

const ErrorTypography = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
  margin: 0 4px;
`;
